import { useEffect, useMemo } from "react";

import { InfinityLoader, ListItemBox } from "common/components";
import { useDispatch, useSelector } from "common/utilities/react-redux";
import { useTranslate } from "common/utilities/useTranslate";
import { getIsAuthenticated } from "domains/authentication/selectors";
import { fetchCaseStudiesById } from "domains/caseStudy/actions";
import { ExpandableCaseStudyItem } from "domains/caseStudy/components/ExpandableCaseStudyItem";
import { createCaseStudiesForTopicSelector, createCaseStudiesSelector } from "domains/caseStudy/selectors";
import { TopicDetailSection } from "domains/topicDetail/components/TopicDetailSection";
import { getTmSelectedId } from "domains/transformationMap/selectors";

type CaseStudySectionProps = { selectedSectionId?: string; selectSectionHandle: (id: string) => void; topicId: string };

const SECTION_ID = "case_studies";
const ITEM_LIMIT = 4;

export const CaseStudySection = ({ topicId, selectedSectionId, selectSectionHandle }: CaseStudySectionProps) => {
  const isExpanded = SECTION_ID === selectedSectionId;

  const dispatch = useDispatch();
  const hasAccess = useSelector(getIsAuthenticated);
  const selectedInnerNodeId = useSelector(getTmSelectedId);
  const translate = useTranslate();

  const id = selectedInnerNodeId ?? topicId;

  const caseStudiesForTopic = useSelector(createCaseStudiesForTopicSelector(id));

  const caseStudiesSelector = useMemo(
    () => createCaseStudiesSelector(caseStudiesForTopic?.caseStudyIds ?? []),
    [caseStudiesForTopic?.caseStudyIds],
  );

  const caseStudies = useSelector(caseStudiesSelector).slice(0, isExpanded ? undefined : ITEM_LIMIT);

  const isHidden = selectedSectionId !== SECTION_ID && !!selectedSectionId;
  const hasNextPage = caseStudiesForTopic?.hasNextPage ?? false;
  const isPending = caseStudiesForTopic?.isPending ?? false;

  const onSelection = () => selectSectionHandle(SECTION_ID);

  useEffect(() => {
    if (id) {
      dispatch(
        fetchCaseStudiesById({
          topicId: id,
          offset: 0,
          limit: ITEM_LIMIT,
        }),
      );
    }
  }, [id, dispatch]);

  if (caseStudies.length === 0 && !isPending) {
    return null;
  }

  const loadNextPage = () => {
    dispatch(
      fetchCaseStudiesById({
        topicId: id,
        offset: caseStudies.length,
        limit: 12,
      }),
    );
  };

  return (
    <TopicDetailSection
      display
      hasNextPage={caseStudies.length >= ITEM_LIMIT}
      isExpanded={isExpanded}
      isHidden={isHidden}
      isLocked={!hasAccess}
      selectSectionHandle={onSelection}
      title={translate("app.case_studies.title")}
      topicId={id}
    >
      <InfinityLoader loadNextPage={loadNextPage} hasNextPage={isExpanded && hasNextPage} isPending={isPending}>
        {caseStudies.map((caseStudy) => (
          <ListItemBox key={caseStudy.id}>
            <ExpandableCaseStudyItem caseStudy={caseStudy} />
          </ListItemBox>
        ))}
      </InfinityLoader>
    </TopicDetailSection>
  );
};
